// Endpoint paths below already include the "/api/..." prefix, so the base must
// be an ORIGIN with no /api suffix (or "" for same-origin) — NOT "/api", which
// would produce a doubled "/api/api/...". Prod is served same-origin behind
// Caddy, so the base is "" there; dev hits the local uvicorn on :8000.
const API_BASE_URL =
  import.meta.env.VITE_TRACE_API_URL ?? (import.meta.env.DEV ? "http://127.0.0.1:8000" : "");
const API_TIMEOUT_MS = 7000;

async function fetchWithTimeout(input: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), API_TIMEOUT_MS);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

type PersonalAdviceResponse = {
  advice: TraceDemoOutput;
  context: unknown;
};

export type PersonalVariables = Record<string, unknown>;

export type PersonalProfile = {
  id: string;
  label: string;
  variables: PersonalVariables;
  relevant_candidates?: string[];
  relevant_actions?: string[];
};

export type FactorSource = {
  id?: string | null;
  label: string;
  url?: string | null;
  domain?: string | null;
  context_id?: string | null;
  source_origin?: "cala" | "web" | string;
  source_provider?: string | null;
  ui_highlight_tags?: string[];
  retrieved_at?: string | null;
  published_at?: string | null;
  language?: string | null;
  evidence_note?: string | null;
  evidence_note_status?: EvidenceStatus | null;
  tagged_evidence_notes?: Array<TaggedEvidenceText>;
  evidence_status_rollup?: Record<string, number>;
  probability_bucket_counts?: Record<string, number>;
  average_probability_pct?: number | null;
  dominant_evidence_status?: string;
};

export type EvidenceStatus = {
  status: string;
  label: string;
  is_already_real: boolean;
  rationale: string;
  probability_pct: number;
  probability_label: string;
  probability_rationale: string;
};

export type TaggedEvidenceText = EvidenceStatus & {
  text: string;
};

export type FactorPolicyChange = {
  date?: string | null;
  title: string;
  expected_effect?: string;
  source_ids?: string[];
  evidence_status?: EvidenceStatus;
};

export type FactorBottleneckOutlook = {
  period?: string;
  expected_change: string;
  confidence?: string;
  rationale?: string;
  source_ids?: string[];
  evidence_status?: EvidenceStatus;
};

export type FactorNode = {
  id: string;
  label: string;
  level: number;
  parent?: string | null;
  query_id?: string | null;
  summary: string;
  mechanism?: string | null;
  direction_on_prices?: string | null;
  metric_count: number;
  source_count: number;
  top_metrics: string[];
  tagged_metrics?: TaggedEvidenceText[];
  sources: FactorSource[];
  raw_file?: string | null;
  policy_changes?: FactorPolicyChange[];
  bottleneck_outlook?: FactorBottleneckOutlook[];
  evidence_status_rollup?: Record<string, number>;
  probability_bucket_counts?: Record<string, number>;
  average_probability_pct?: number | null;
  dominant_evidence_status?: string;
  provenance?: {
    derived_from_source_origins?: string[];
    ui_highlight_tags?: string[];
    raw_response?: string | null;
    status?: string;
    note?: string;
    generated_at?: string;
  };
};

export type FactorResearch = {
  case: string;
  retrieved_at?: string;
  generated_from?: string;
  coverage_status: string;
  coverage_note?: string;
  rollups: {
    factor_count?: number;
    level_counts?: Record<string, number>;
    raw_response_count?: number;
    metric_line_count?: number;
    source_link_count?: number;
    direct_cala_source_link_count?: number;
    web_source_link_count?: number;
    web_registry_source_count?: number;
    web_nl_registry_source_count?: number;
    evidence_status_counts?: Record<string, number>;
    probability_bucket_counts?: Record<string, number>;
    average_probability_pct?: number | null;
    metric_evidence_status_counts?: Record<string, number>;
    source_evidence_status_counts?: Record<string, number>;
    policy_evidence_status_counts?: Record<string, number>;
    outlook_evidence_status_counts?: Record<string, number>;
  };
  edges: Array<{
    source: string; target: string;
    relation?: "contains" | "causal" | "confounder" | "feedback" | "conditioning";
    sign?: "+" | "-" | "?" | string;
    strength?: string;
    mechanism?: string;
    contested?: boolean;
  }>;
  factors: FactorNode[];
  web_sources?: FactorSource[];
  evidence_status_taxonomy?: Record<string, { label: string; is_already_real: boolean; description: string }>;
  probability_scoring?: Record<string, { base_pct: number; label: string; description: string }>;
};

export async function runPersonalAdvice(
  personalVariables: PersonalVariables | null = null
): Promise<TraceDemoOutput> {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/run-personal-advice`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      case: "nl_housing",
      personal_variables: personalVariables ?? {},
      expert_notes: []
    })
  });

  if (!response.ok) {
    throw new Error(`Trace API returned ${response.status}`);
  }

  const payload = (await response.json()) as PersonalAdviceResponse;
  return payload.advice;
}

// ---- additional engine endpoints (PRD §15 API boundary) -------------------
// Engine output is open-shaped while protocol names settle.
export type EngineOutput = Record<string, any>;

export type RunRequest = {
  case?: string;
  personal_variables?: Record<string, unknown> | null;
  expert_notes?: unknown[] | null;
};

async function postRun(path: string, body: RunRequest = {}): Promise<EngineOutput> {
  const response = await fetchWithTimeout(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ case: "nl_housing", ...body })
  });
  if (!response.ok) {
    throw new Error(`Trace API ${path} returned ${response.status}`);
  }
  return (await response.json()) as EngineOutput;
}

/** Health + which input source the engine is using (fixtures vs run_demo). */
export async function health(): Promise<EngineOutput> {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/health`);
  if (!response.ok) throw new Error(`Trace API health returned ${response.status}`);
  return (await response.json()) as EngineOutput;
}

export async function fetchFactorTree(): Promise<FactorResearch> {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/factor-tree`);
  if (!response.ok) throw new Error(`Trace API factor-tree returned ${response.status}`);
  return (await response.json()) as FactorResearch;
}

export async function fetchPersonalProfiles(): Promise<PersonalProfile[]> {
  const response = await fetchWithTimeout(`${API_BASE_URL}/api/personal-profiles`);
  if (!response.ok) throw new Error(`Trace API personal-profiles returned ${response.status}`);
  const payload = (await response.json()) as { profiles: PersonalProfile[] };
  return payload.profiles;
}

/** Default tier: distribution / coverage / credibility / candidate_status. */
export async function runDefaultTier(body?: RunRequest): Promise<EngineOutput> {
  return (await postRun("/api/run-default-tier", body)).default;
}

/** Structured tier: structurally_established | refuted | graph_not_settled + gap. */
export async function runStructuredTier(body?: RunRequest): Promise<EngineOutput> {
  return (await postRun("/api/run-structured-tier", body)).structured;
}

/** Bridge: integrated claim_resolution (keeps support and causal resolution apart). */
export async function runBridge(body?: RunRequest): Promise<EngineOutput> {
  return (await postRun("/api/run-bridge", body)).merged;
}

export const engineBaseUrl = API_BASE_URL;
